import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import API from "@/lib/api";
import { ClipboardCheck, LifeBuoy, Clock, CheckCircle2, AlertTriangle, FileText, ArrowRight } from "lucide-react";

const STATUS_STYLE = {
  draft: { label: "Not Started", color: "bg-slate-500/10 text-slate-400 border-slate-500/30", icon: FileText },
  submitted: { label: "Awaiting Review", color: "bg-amber-500/10 text-amber-500 border-amber-500/30", icon: Clock },
  needs_revision: { label: "Needs Revision", color: "bg-rose-500/10 text-rose-500 border-rose-500/30", icon: AlertTriangle },
  approved: { label: "Approved", color: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30", icon: CheckCircle2 },
};

export default function ProspectiveClientDashboard() {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/onboarding/me")
      .then((r) => setSub(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const status = sub?.status || "draft";
  const StatusIcon = STATUS_STYLE[status]?.icon || FileText;

  const HERO_COPY = {
    draft: {
      eyebrow: "Fundtec Onboarding",
      title: "Welcome to Fundtec!",
      body: "We're excited to have you on board. To help you get started and make the most of our platform, we require some information about you and your legal entity. Kindly complete all sections of the onboarding form at your earliest convenience. This information is required for the Fundtec team to proceed with due diligence and contract formalities.",
    },
    submitted: {
      eyebrow: "Submission Received",
      title: "Thanks — we're reviewing your details",
      body: "Your onboarding submission has been received by the Fundtec team. We're now conducting our due diligence and contract formalities. You'll be notified by email at the company and contact addresses on file as soon as your account is approved. In the meantime, feel free to raise a support ticket if anything comes up.",
    },
    needs_revision: {
      eyebrow: "Revisions Requested",
      title: "A small update is needed",
      body: "Our team has reviewed your submission and requested a few revisions. Please open the onboarding form, address the feedback, and resubmit at your earliest convenience.",
    },
    approved: {
      eyebrow: "Welcome Aboard",
      title: "You're approved 🎉",
      body: "Your onboarding has been approved by the Fundtec team. Please sign out and sign back in to access your fund administration workspace.",
    },
  };
  const hero = HERO_COPY[status] || HERO_COPY.draft;

  return (
    <div className="space-y-8 max-w-4xl" data-testid="prospective-dashboard">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 p-8 sm:p-10">
        <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-indigo-500/10 blur-3xl pointer-events-none" />
        <div className="relative">
          <p className="text-xs uppercase tracking-[0.2em] text-indigo-300/80 font-medium" data-testid="welcome-eyebrow">{hero.eyebrow}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mt-3">
            {hero.title}
          </h1>
          <p className="text-slate-300 text-base mt-3 max-w-2xl leading-relaxed">
            {hero.body}
          </p>
          <div className="mt-6 flex items-center gap-3">
            <Badge variant="outline" className={`${STATUS_STYLE[status]?.color} border text-xs px-3 py-1`} data-testid="onboarding-status-badge">
              <StatusIcon className="w-3.5 h-3.5 mr-1.5 inline" /> {STATUS_STYLE[status]?.label || status}
            </Badge>
            {sub?.submitted_at && status !== "approved" && (
              <span className="text-xs text-slate-400">
                Submitted {new Date(sub.submitted_at).toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Status-specific notice */}
      {status === "submitted" && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="py-5 flex gap-4 items-start">
            <Clock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-500">We're working on your onboarding</p>
              <p className="text-sm text-muted-foreground mt-1">Our team is reviewing your submission. You'll be notified as soon as it's approved and full access is granted. In the meantime, feel free to raise a support ticket if you have questions.</p>
            </div>
          </CardContent>
        </Card>
      )}
      {status === "needs_revision" && (
        <Card className="border-rose-500/30 bg-rose-500/5">
          <CardContent className="py-5">
            <div className="flex gap-4 items-start">
              <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-rose-500">Revisions Requested</p>
                {sub?.revision_notes && (
                  <p className="text-sm text-foreground mt-2 whitespace-pre-wrap leading-relaxed">{sub.revision_notes}</p>
                )}
                <p className="text-xs text-muted-foreground mt-3">Please update your submission and resubmit.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {status === "approved" && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="py-5 flex gap-4 items-start">
            <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-emerald-500">Onboarding Approved</p>
              <p className="text-sm text-muted-foreground mt-1">Please sign out and sign back in to access your fund management workspace.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card className="group hover:border-primary/50 transition-colors" data-testid="tile-onboarding">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5 text-indigo-500" />
              </div>
              <div>
                <CardTitle className="text-base">Onboarding Form</CardTitle>
                <CardDescription className="text-xs">Legal entity, signatories, contacts & documents</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Link to="/onboarding">
              <Button variant="secondary" className="w-full justify-between group-hover:bg-primary group-hover:text-primary-foreground transition-colors" data-testid="open-onboarding-btn" disabled={loading}>
                {status === "draft" && "Start Onboarding"}
                {status === "needs_revision" && "Continue Revisions"}
                {status === "submitted" && "Review Submission"}
                {status === "approved" && "View Submission"}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="group hover:border-primary/50 transition-colors" data-testid="tile-support">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-500/10 flex items-center justify-center">
                <LifeBuoy className="w-5 h-5 text-rose-500" />
              </div>
              <div>
                <CardTitle className="text-base">Need Help?</CardTitle>
                <CardDescription className="text-xs">Raise a support ticket with our team</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Link to="/support">
              <Button variant="secondary" className="w-full justify-between group-hover:bg-primary group-hover:text-primary-foreground transition-colors" data-testid="open-support-btn">
                Open Support
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
